import AdvanceExpense from '@/components/applications/advance-expense/AdvanceExpense.vue';
import RepresentationExpenses from '@/components/applications/representation-expenses/RepresentationExpenses.vue';
import moment from 'moment';
import Decisions from '@/components/common/decision/Decisions.vue';
import Approvers from '@/components/common/approvers/Approvers.vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import Fileupload from '@ebg/vue-common/src/components/common/fileupload/Fileupload.vue';
import TextEditor from '@ebg/vue-common/src/components/common/texteditor/TextEditor.vue';
import AdvanceExpenseRequestPreview from '@/components/applications/advance-expense-request-preview/AdvanceExpenseRequestPreview.vue';

const formLine = {
  id: null,
  representation_expense_id: null,
  document_date: null,
  bill_number: null,
  provider: null,
  amount: null,
  advance_expense_payment_type_id: null,
  payment_document_type_id: null,
  reg_no: null,
  payout_amount: null,
  tax_amount: null,
  currency: null,
  description: null,
  dimension_division: null,
  dimension_cost_center: null,
  decisions: null,
  user_decisions: null,
  title: null,
  ax_account_id: null,
  files: [
    {
      id: null,
      title: null,
      locale: null,
      document_type: null,
      document_type_id: null,
      file: null,
      filename: null
    }
  ],
  decision:
    {
      status: false,
      show_comment: false,
      comment: null
    },
  new_decision_comment: null,
  representation_expense_decision:
    {
      status: false
    },
  saving_check: false,
  amount_without_tax: 0,
  parent_id: null,
  show_children: false,
  children: [
    {
      id: null,
      title: null,
      amount_without_tax: null,
      tax_amount: null,
      currency: null,
      dimension: {
        division: null,
        cost_center: null
      },
      advance_expense_payment_type_id: null
    }
  ],
  errors: {
    taxAmount: false,
    withoutTaxAmount: false,
    lineSumTaxAmount: null,
    lineSumWithoutTaxAmount: null
  },
  wholeCheckAsLine: true
}

// a line is only required to be complete once it has been "saved" (has an id, see
// isLineSaved()); an unsaved line (e.g. a leftover blank row, or a check still being
// split/processed) must not block saving the rest of the report
const requiredIfLineSaved = (value, siblingState) => {
  if (!siblingState?.id) {
    return true;
  }
  return helpers.req(value);
};

const mapAxAccountToDocumentType = {
  82901: 'REPREZ_IZDEV_NOR_AKTS',
  82400: 'REPREZ_IZDEV',
  82900: 'REPREZ_IZDEV_NOR_AKTS'
}

export default {
  name: 'AdvanceExpenseRequest',
  components: { AdvanceExpenseRequestPreview, Approvers, Decisions, AdvanceExpense, RepresentationExpenses, Fileupload, TextEditor },
  props: {
    preview: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dataId: null,
      randomKey: 1,
      currentReqSaveCheck: null,
      manualCheckInput: false,
      record: null,
      recordValues: null,
      isLoading: false,
      accept_terms: false,
      representation_expense_id: null,
      representation_expense_status: null,
      line_id: null,
      document_type: null,
      form: {
        period_start: null,
        period_end: null,
        primary_payment_type_id: null,
        payment_method_id: null,
        amount_private: null,
        lines: [{ ...formLine }],
        approvers: [],
        comment: null
      },
      waitingDocumentAIResponse: false,
      canEditForm2: true,
      fileUuid: null,
      backendUrl: null,
      windowHeight: 200,
      recordFields: {
        amount: null,
        comment: null,
        user_bank: null,
        bank: null,
        bank_account: null,
        purpose: null
      },
      hasFormValuesChanged: false,
      chequeLines: null
    }
  },
  validations () {
    return {
      form: {
        period_start: { required },
        period_end: { required },
        payment_method_id: { required },
        ...(this.allowLinesValidation
          ? {
              lines: {
                $each: helpers.forEach({
                  document_date: { required: requiredIfLineSaved },
                  bill_number: { required: requiredIfLineSaved },
                  reg_no: { required: requiredIfLineSaved },
                  amount_without_tax: { required: requiredIfLineSaved },
                  tax_amount: { required: requiredIfLineSaved },
                  amount: { required: requiredIfLineSaved },
                  ax_account_id: { required: requiredIfLineSaved },
                  dimension_division: { required: requiredIfLineSaved },
                  dimension_cost_center: { required: requiredIfLineSaved },
                  payment_document_type_id: { required: requiredIfLineSaved }
                }),
                $lazy: true,
                $autoDirty: true
              }
            }
          : {})
      }
    }
  },
  computed: {
    allowLinesValidation () {
      return !!this.form.lines[0]?.id || this.manualCheckInput;
    },
    id () {
      const id = this.dataId ?? this.$route.params.id;
      if (id === 'NEW') {
        return id;
      }
      if (Number.isInteger(parseInt(id))) {
        return parseInt(id);
      }
    },
    componentName () {
      if (this.isModal) {
        return 'b-modal';
      }
      return 'div';
    },
    title () {
      let title = 'default';
      if (this.form.payment_method_id) {
        const paymentMethodCode = this.paymentMethods?.find(method => method.value === this.form.payment_method_id)?.code;
        if (paymentMethodCode === 'BUSINESS_TRIP') {
          title = 'business-trip';
        }
      }
      return this.$t('applications.advance-expense.title-' + title);
    },
    axAccounts () {
      return this.$store.getters.getConfigOptions('ax_accounts', this.$i18n.locale);
    },
    dimensionDivisions () {
      return this.$store.getters.getConfigOptions('dimensions_divisions', this.$i18n.locale);
    },
    dimensionCostCenters () {
      return this.$store.getters.getConfigOptions('dimensions_cost_centers', this.$i18n.locale);
    },
    paymentPurposes () {
      return this.$store.getters.getConfigOptions('advance_expense_purposes', this.$i18n.locale);
    },
    iframeHeight () {
      return this.windowHeight - 200;
    },
    fileSourceUrl () {
      return this.backendUrl + 'view-file?uuid=' + this.fileUuid
    },
    formCurrency () {
      return this.form.lines[0].currency ?? null;
    },
    payoutAmountWithoutTaxTotal () {
      let total = this.form.lines.reduce((amount, line) => {
        return amount + Number(line.amount_without_tax || 0)
      }, 0)
      total = total * 100 / 100;
      return total.toFixed(2);
    },
    // built from the lines' net and VAT, so a corrected line counts instead of the originally recognized total
    payoutAmountTotal () {
      return (Number(this.payoutAmountWithoutTaxTotal) + Number(this.taxAmount)).toFixed(2);
    },
    taxAmount () {
      let total = this.form.lines.reduce((amount, line) => {
        return amount + Number(line.tax_amount || 0)
      }, 0)
      total = total * 100 / 100;
      return total.toFixed(2);
    },
    amountTotal () {
      let total = this.form.lines.reduce((amount, line) => {
        return amount + Number(line.amount || 0)
      }, 0)
      total = total * 100 / 100;
      return total.toFixed(2);
    },
    lineFieldRequired () {
      // at first isnt required because we wait response from AI document service
      return true;
    },
    status () {
      if (this.form.status_id) {
        return this.$store.getters.getParamFromConfigByFilter('advance_expense_request_statuses', 'id', parseInt(this.form.status_id), 'code').toLowerCase();
      }
    },
    isDraft () {
      if (this.isWithoutId || ['draft'].includes(this.status)) {
        return true;
      }
    },
    isReviewable () {
      if (this.isInReview && this.form.waiting_approval_from == this.$session.get('userId')) {
        return true;
      }
    },
    isInReview () {
      if (this.status && ['review'].includes(this.status)) {
        return true;
      }
    },
    canEditForm () {
      if (!this.canEditForm2) {
        return false;
      }
      if (this.isWithoutId || (['draft', 'changes_needed'].includes(this.status) && this.form.user_id == this.$session.get('userId'))) {
        return true; //
      }
    },
    isWithoutId () { // form opened from list
      return this.$route.params.id === 'NEW';
    },
    areChangesNeeded () {
      return this.status === 'changes_needed';
    },
    canEdit () {
      return true; // @TODO
    },
    requestPath () {
      return 'advance-forms/expense/' + this.id;
    },
    paymentMethods () {
      // only these two payment methods should be selectable for new choices; other
      // entries that may exist in the config table are intentionally hidden from
      // this dropdown - but if the record already has one of them set (e.g. the
      // system-assigned BASED_ON_ADVANCE default for record-linked requests, see
      // loadData()), keep it in the list so it still renders instead of showing blank
      const visibleCodes = ['ADVANCE_EXPENSE', 'BUSINESS_TRIP'];
      const options = this.$store.getters.getConfigOptions(
        'advance_expense_payment_methods', this.$i18n.locale, 'code', visibleCodes, false, false, true
      );
      if (this.form.payment_method_id && !options.find(o => o.value === this.form.payment_method_id)) {
        const allOptions = this.$store.getters.getConfigOptions('advance_expense_payment_methods', this.$i18n.locale);
        const currentOption = allOptions.find(o => o.value === this.form.payment_method_id);
        if (currentOption) {
          options.push(currentOption);
        }
      }
      return options;
    },
    paymentTypes () {
      return this.$store.getters.getConfigOptions('advance_expense_payment_types', this.$i18n.locale);
    },
    documentTypes () {
      return this.$store.getters.getConfigOptions('advance_expense_document_types', this.$i18n.locale);
    },
    // a business trip order belongs only to a business trip report
    lineDocumentTypes () {
      const paymentMethodCode = this.paymentMethods?.find(method => method.value === this.form.payment_method_id)?.code;
      return this.documentTypes.filter(item => item.code !== 'BUSINESS_TRIP_ORDER' || paymentMethodCode === 'BUSINESS_TRIP');
    },
    validMinDate () {
      return moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
    },
    validMaxDate () {
      return moment().endOf('month').format('YYYY-MM-DD');
    },
    apstatus () {
      return {
        value: '0',
        bicon: 'check'
      }
    },
    fileUploadDocumentTypeIds () {
      return this.documentTypes
        .filter(item => ['FUEL', 'RECEIPT', 'BUSINESS_TRIP_ORDER'].includes(item.code))
        .map(item => item.value);
    }
  },
  methods: {
    showModal (el) {
      this.dataId = el.id;
      this.loadData();
      this.$refs['modal-window'].show();
    },
    getLineChildren (lines) {
      return lines.filter((line) => line.send_to_support === true);
    },
    changeAxAccount (index, axAccountId) {
      const key = this.axAccounts.find(el => el.value === axAccountId)?.code;
      if (key && mapAxAccountToDocumentType[key]) {
        const documentTypeId = this.documentTypes.find(el => el.code === mapAxAccountToDocumentType[key])?.value;
        if (documentTypeId) {
          this.form.lines[index].payment_document_type_id = documentTypeId;
        }
      }
    },
    toggleLineChildren (index) {
      this.form.lines[index].show_children = !this.form.lines[index].show_children;
      this.$forceUpdate(); // @TODO fix shallow copy reactivity
    },
    updateChildrenDimensionCostCenter (index, value) {
      this.form.lines[index].children.forEach(child => {
        if (!child.dimension) {
          this.$set(child, 'dimension', { division: null, cost_center: null });
        }
        child.dimension.cost_center = value;
      });
    },
    updateChildrenDimensionDivision (index, value) {
      this.form.lines[index].children.forEach(child => {
        if (!child.dimension) {
          this.$set(child, 'dimension', { division: null, cost_center: null });
        }
        child.dimension.division = value;
      });
    },
    updateChildrenPaymentTypes (index, value) {
      this.form.lines[index].children.forEach(child => {
        child.advance_expense_payment_type_id = value;
      });
    },
    updateChildrenCurrency (index, value) {
      this.form.lines[index].children.forEach(child => {
        child.currency = value;
      });
    },
    onResize (e) {
      this.windowHeight = window.innerHeight;
    },
    showFile (uuid) {
      if (uuid) {
        this.fileUuid = uuid;
        this.$refs.file.show();
      }
    },
    isLineSaved (line) {
      return !!line.id;
    },
    fileUpload (event, lineId) {
      if (event?.file && !this.currentReqSaveCheck) {
        // this.saveCheck(lineId);
      }
    },
    saveCheck (index) {
      if (this.form.lines[index].saving_check) {
        return;
      }
      this.form.agreed_terms = this.accept_terms;
      const reqData = this.createFormData(this.form, ['period_start', 'period_end', 'primary_payment_type_id', 'payment_method_id', 'amount_private', 'agreed_terms', 'lines'], 'form', this, false);
      if (this.isWithoutId) {
        reqData.append('record', JSON.stringify(this.recordValues));
      }
      reqData.append('line_index', index);
      reqData.append('mode', 'upload_check');
      reqData.append('wholeCheckAsLine', this.form.lines[index].wholeCheckAsLine);
      this.form.lines[index].saving_check = true;
      this.currentReqSaveCheck = this.ApiRequest.request(this.requestPath, this.ApiRequest.REQUEST_FILEUPLOAD, reqData, (response) => {
        if (response.error) {
          this.ApiRequest.displayErrorDialog(response, this);
        } else if (response.data) {
          let aiReponse = null;
          if (response.data.lines[index]?.files[0]?.response) {
            aiReponse = response.data.lines[index]?.files[0]?.response.response.extracted_data;
          }
          if (!aiReponse) {
            this.$store.commit('toastAdd', { context: 'danger', message: this.$t('Čeka atpazīšana neizdevās. Lūdzu, ievadiet čeka datus manuāli') })
            this.manualCheckInput = true;
          } else {
            this.manualCheckInput = false;
          }
          this.form = response.data;
          this.form.lines.forEach((line, lineIndex) => {
            this.normalizeLine(line, lineIndex === index);
            this.checkLineAmountsAgainstTotal(lineIndex);
          });
        }
        this.form.lines[index].saving_check = false;
      });
    },
    normalizeLine (line, resetFiles = false) {
      if (!line.decision) {
        this.$set(line, 'decision', {
          status: false,
          show_comment: false,
          comment: null
        });
      }
      if (resetFiles || !line.files) {
        this.$set(line, 'files', null);
      }
      this.$set(line, 'errors', {
        taxAmount: false,
        withoutTaxAmount: false,
        lineSumTaxAmount: null,
        lineSumWithoutTaxAmount: null
      });
      this.$set(line, 'dimension_division', line.dimension ? line.dimension.division : null);
      this.$set(line, 'dimension_cost_center', line.dimension ? line.dimension.cost_center : null);
      if (!line.children) {
        this.$set(line, 'children', []);
      }
      line.children.forEach(child => {
        if (!child.dimension) {
          this.$set(child, 'dimension', { division: null, cost_center: null });
        }
      });
    },
    loadData () {
      if (this.form?.lines?.length < 1) {
        // this.addLine();
      }
      if (this.$route.params.id === 'NEW') {
        this.isLoading = true;
        this.ApiRequest.request(this.requestPath + '/user-data', this.ApiRequest.REQUEST_GET, [], (response) => {
          if (response.data) {
            this.record = response.data;
            // this.form.lines = [];
          }
        });
        this.isLoading = false;
      }
      if (!this.isWithoutId) {
        this.isLoading = true;
        this.ApiRequest.request(this.requestPath, this.ApiRequest.REQUEST_GET, [], (response) => {
          if (response.data) {
            this.form = response.data;
            this.form.lines = response.data.lines;
            this.form.lines.forEach((line, index) => {
              this.normalizeLine(line, true);
              this.checkLineAmountsAgainstTotal(index);
            })
            if (response.data.record) {
              this.record = response.data.record;
              this.record.code = response.data.code;
              this.record.register_date = response.data.createdDate;
              this.record.job_position_id = response.data.company_job_position_id;
              this.form.payment_method_id = this.$store.getters.getParamFromConfigByFilter('advance_expense_payment_methods', 'code', 'BASED_ON_ADVANCE', 'id');
            } else {
              // this.record = response.data.record_data;
              // this.record.code = response.data.record_data.code;
            }

            if (this.form.lines.length < 0) {
              // this.addLine();
            }

            if (response.data.record_fields && typeof response.data.record_fields === 'object') {
              this.recordFields = response.data.record_fields;
            }
            if (response.data.agreed_terms) {
              this.accept_terms = true;
            }
          }
          this.isLoading = false;
          if (this.form.lines.length < 1) {
            // this.addLine();
          }
        })
      }
    },
    removeLine (index) {
      if (this.form.lines[index].files) {
        this.form.lines[index].files = null;
      }
      this.form.lines.splice(index, 1);
    },
    removeLineChildren (index, childrenIndex) {
      this.form.lines[index].children.splice(childrenIndex, 1);
    },
    addLineChildren (index) {
      const line = this.form.lines[index];
      const firstChild = line.children && line.children.length ? line.children[0] : line;
      line.children.push(
        {
          id: null,
          title: null,
          amount_without_tax: null,
          tax_amount: null,
          currency: line.currency,
          dimension: {
            division: line.dimension_division ?? null,
            cost_center: line.dimension_cost_center ?? null
          },
          advance_expense_payment_type_id: line.advance_expense_payment_type_id,
          send_to_support: true,
          parent_id: line.id,
          advance_expense_request_id: this.id,
          provider: firstChild.provider,
          bill_number: firstChild.bill_number,
          document_date: firstChild.document_date,
          reg_no: firstChild.reg_no,
          tax_no: firstChild.tax_no
        }
      );
    },
    addLine () {
      if (this.form.primary_payment_type_id) {
        formLine.advance_expense_payment_type_id = this.form.primary_payment_type_id;
      }
      const newLine = JSON.parse(JSON.stringify(formLine));
      newLine.dimension_division = this.form.default_dimension?.division ?? null;
      newLine.dimension_cost_center = this.form.default_dimension?.cost_center ?? null;
      this.form.lines.push(newLine);
    },
    // two separate <AdvanceExpense> instances render in this form (the head section,
    // and - when amount_private > 0 - a second "hide-head" instance for the
    // bank/private-reimbursement section). Both declare their own required
    // job_position_id validator feeding into this component's collected v$, but the
    // second instance hides that field entirely, so it can never be filled in through
    // the UI. Resolve both (falling back to copying the head's value) before touching.
    resolveJobPositionAndTouch () {
      const headRef = this.$refs.advanceExpenseHead;
      const bankRef = this.$refs.advanceExpenseBank;
      headRef?.setJobPosition();
      bankRef?.setJobPosition();
      if (bankRef && !bankRef.form.job_position_id && headRef?.form?.job_position_id) {
        bankRef.form.job_position_id = headRef.form.job_position_id;
      }
      this.v$.$touch();
    },
    async fillAct (index, status = null) {
      this.resolveJobPositionAndTouch();
      if (this.form.lines[index].id === null) {
        if (this.v$.$invalid || this.isLoading) {
          return;
        }
        const result = await this.saveFormLine(index);
        this.form.lines[index].id = result.data.id;
      }
      this.line_id = this.form.lines[index].id;
      this.document_type = this.form.lines[index].payment_document_type_id;
      this.representation_expense_id = this.form.lines[index].representation_expense_id;
      this.representation_expense_status = status;
      this.chequeLines = this.form.lines[index];
      this.$refs.modalxl.show();
    },
    addFuelTicket (index, id) {
      //
    },
    async saveFormLine (index, id) {
      try {
        return new Promise((resolve, reject) => {
          this.isLoading = true;
          const reqData = this.BasicHelper.cloneObject(this.form.lines[index]);
          this.ApiRequest.request(this.requestPath + '/lines/', this.ApiRequest.REQUEST_POST, reqData, (response) => {
            this.isLoading = false;
            if (response.error) {
              this.ApiRequest.displayErrorDialog(response, this);
              reject(response.error);
            }
            resolve(response);
          });
        });
      } catch (e) {
        console.error(e);
      }
    },
    changePrimaryPaymentType (typeId) {
      this.form.lines.filter(e => e.payment_type < 1).forEach(el => {
        el.payment_type = typeId;
      });
    },
    cancel () {
      this.$router.push({ name: 'menu.forms.advance-expenses' });
    },
    save (afterSave = null) {
      this.resolveJobPositionAndTouch();

      if (this.v$.$invalid) {
        this.$store.commit('toastAdd', { context: 'danger', message: this.$t('general.form-invalid') });
        return;
      }

      if (!this.isLoading) {
        this.isLoading = true;
        this.waitingDocumentAIResponse = true;
        this.canEditForm2 = false;
        this.form.agreed_terms = this.accept_terms;
        const reqData = this.createFormData(this.form, ['period_start', 'period_end', 'primary_payment_type_id', 'payment_method_id', 'amount_private', 'agreed_terms', 'lines'], 'form', this, false);
        if (this.isWithoutId) {
          reqData.append('record', JSON.stringify(this.recordValues));
        }

        reqData.append('record_fields', JSON.stringify(this.recordFields));

        this.ApiRequest.request(this.requestPath, this.ApiRequest.REQUEST_FILEUPLOAD, reqData, (response) => {
          if (response.error) {
            this.ApiRequest.displayErrorDialog(response, this);
          } else {
            this.form = response.data;
            this.$store.commit('toastAdd', { context: 'success', message: this.$t('applications.advance-expense.saved') });
            if (!this.isWithoutId) {
              // stay on the report the user just saved, so they can carry on editing it or send
              // it for review without walking back in through the list. The saved lines come
              // straight from the server, so they need the same normalizing as on load -
              // without it the dimension selects would come back empty
              this.form.lines.forEach((line, lineIndex) => {
                this.normalizeLine(line, true);
                this.checkLineAmountsAgainstTotal(lineIndex);
              });
              if (afterSave) {
                this.$nextTick(afterSave);
              }
            } else {
              const routeData = this.$router.resolve({ name: 'menu.forms.advance-expense-request', params: { id: response.data.id } });
              // this.$router.push(routeData.href);
              window.open(routeData.href, '_self');
            }
          }
          this.waitingDocumentAIResponse = false;
          this.canEditForm2 = true;
          this.isLoading = false;
        });
      }
    },
    changeStatus (status, approve = null, saved = false) {
      // submitting for review (not the reviewer's approve/reject actions, which pass
      // `approve`) must go through the same line-completeness validation as save() -
      // this button previously bypassed it entirely, letting incomplete lines (missing
      // reg_no, VG kategorija, dimensions, etc.) be sent for approval unchecked
      if (status === 'review' && !approve) {
        this.resolveJobPositionAndTouch();
        if (this.v$.$invalid) {
          this.$store.commit('toastAdd', { context: 'danger', message: this.$t('general.form-invalid') });
          return;
        }
        if (!this.form.lines.length) {
          this.$store.commit('toastAdd', { context: 'danger', message: this.$t('api.advance-expense-no-lines') });
          return;
        }
        // what the user typed since the last save is sent for review too, not only what was saved
        if (!saved) {
          this.save(() => this.changeStatus(status, approve, true));
          return;
        }
      }
      if (!this.isLoading) {
        this.isLoading = true;
        const reqData = { status: status };
        if (approve) {
          reqData.approve = approve;
        }

        if (this.isReviewable) {
          reqData.decisions = [];
          reqData.comment = this.form.comment;
          this.form.lines.forEach((line) => {
            reqData.decisions.push({
              line_id: line.id,
              // approving the request approves the whole document; the per-line marks only
              // say which lines are wrong when it is sent back for correction
              status: approve ? true : line.decision.status,
              comment: line.new_decision_comment
            });
          });
        }
        this.ApiRequest.request(this.requestPath + '/decision', this.ApiRequest.REQUEST_PATCH, reqData, (response) => {
          if (response.error) {
            this.ApiRequest.displayErrorDialog(response, this);
          } else {
            if (response.data === 'status-changed') {
              const routeData = this.$router.resolve({ name: 'menu.forms.advance-expense-applications.list', params: { formType: 'advance-expense' } });
              this.$router.push(routeData.href);
              this.$store.commit('toastAdd', { context: 'success', message: this.$t('applications.advance-expense.status-changed') });
            }
          }
          this.isLoading = false;
        });
      }
    },
    editComment (lineId) {
      this.form.lines[lineId].decision.show_comment = true;
    },
    closeRepresentationExpense (event) {
      this.$refs.modalxl.hide();
    },
    changeRepresentationExpenseStatus (event) {
      this.form.lines.find(
        el => el.id === event.line_id
      ).representation_expense_decision.status = (event.status === 'accept');
      this.$store.commit('toastAdd', {
        context: 'success',
        message: this.$t('applications.representation-expenses.statuses.' + event.status)
      });
      this.$refs.modalxl.hide();
    },
    saveRepresentationExpense (event) {
      this.form.lines.find(el => el.id === event.data.advance_expense_request_line_id)
        .representation_expense_id = event.data.id;
      this.$store.commit('toastAdd', { context: 'success', message: this.$t('applications.representation-expenses.saved') });
      this.closeRepresentationExpense();
    },
    downloadfile (index, key) {
      this.form.lines[index].files[key].loading = true;
      // this.$refs.fileupload.forceUpdate(key);
      this.ApiRequest.request(`advance-forms/expense/${this.form.id}/files/${this.form.lines[index].files[key].id}/download/`, this.ApiRequest.REQUEST_DOWNLOAD, [], () => {
        this.form.lines[index].files[key].loading = false;
        // this.$refs.fileupload.forceUpdate(key);
      });
    },
    createFormData (editEl, keys, basekey, component, fileRequired = true, fileParams = []) {
      const jsonData = {};
      for (const key of keys) {
        if (key !== 'files') {
          jsonData[key] = editEl[key]
        }
      }

      const reqData = new FormData();
      if (Object.keys(jsonData).length > 1) {
        reqData.append(basekey, JSON.stringify(jsonData));
      }

      let hasErrors = false;
      if (editEl.lines) {
        let fileKey = 0;
        editEl.lines.forEach((line, lineKey) => {
          if (line.files) {
            line.files.forEach((el, key) => {
              if (el.deleted || (fileRequired && el.id && !el.file)) {
                const fileJsonData = {
                  id: el.id,
                  deleted: true
                }
                fileParams.forEach(param => {
                  if (el[param] != undefined) {
                    fileJsonData[param] = el[param];
                  }
                });
                fileJsonData.line_id = line.id ?? null;
                fileJsonData.line_index = lineKey;
                reqData.append(basekey + '_filedata[' + fileKey + ']', JSON.stringify(fileJsonData));
              } else if (el.filename || el.file) {
                if (el.file) {
                  reqData.append(basekey + '_files[' + fileKey + ']', el.file);
                }
                const fileJsonData = {
                  id: el.id,
                  title: el.title,
                  locale: (el.locale ? el.locale : null),
                  document_type: (el.document_type ? el.document_type : null),
                  document_type_id: (el.document_type_id ? el.document_type_id : null),
                  line_id: line.id ?? null,
                  line_index: lineKey
                };
                fileParams.forEach(param => {
                  if (el[param] != undefined) {
                    fileJsonData[param] = el[param];
                  }
                });
                reqData.append(basekey + '_filedata[' + fileKey + ']',
                  JSON.stringify(fileJsonData)
                );
              } else if (fileRequired && !hasErrors) {
                // this.displayErrorDialog({ message: component.$t('general.validator.file-required') }, component);
                hasErrors = true;
              }
              fileKey++;
            });
          }
        });
      }
      if (hasErrors) {
        return null;
      }
      return reqData;
    },
    isLineDisabled (line) {
      return false;
    },
    lineHasError (index, field) {
      if (!this.v$.$error || !this.v$.form.lines) {
        return false;
      }
      const errors = this.v$.form.lines.$each?.$response?.$errors;
      return !!(errors && errors[index] && errors[index][field] && errors[index][field].length);
    },
    checkLineAmountsAgainstTotal (index) {
      const totalWithoutTaxAmount = this.form.lines[index].amount_without_tax;
      const totalTaxAmount = this.form.lines[index].tax_amount;
      const linesWithoutTaxAmount = this.form.lines[index].children.filter((el) => (el.send_to_support)).reduce(
        (amount, children) =>
          amount + Math.round(Number(children.amount_without_tax || 0) * 10000),
        0
      ) / 10000;
      const linestotalTaxAmount = this.form.lines[index].children.filter((el) => (el.send_to_support)).reduce(
        (amount, children) =>
          amount + Math.round(Number(children.tax_amount || 0) * 10000),
        0
      ) / 10000;

      if (totalWithoutTaxAmount != linesWithoutTaxAmount) {
        this.form.lines[index].errors.withoutTaxAmount = true;
        this.form.lines[index].errors.lineSumWithoutTaxAmount = linesWithoutTaxAmount;
      } else {
        this.form.lines[index].errors.withoutTaxAmount = false;
      }

      if (totalTaxAmount != linestotalTaxAmount) {
        this.form.lines[index].errors.taxAmount = true;
        this.form.lines[index].errors.lineSumTaxAmount = linestotalTaxAmount;
      } else {
        this.form.lines[index].errors.taxAmount = false;
      }
    }
  },
  setup () {
    return { v$: useVuelidate() }
  },
  created () {
    window.addEventListener('resize', this.onResize);
  },
  destroyed () {
    window.removeEventListener('resize', this.onResize);
  },
  mounted () {
    this.backendUrl = (process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL : '').replace('{BASEURL}', window.location.host);
    this.windowHeight = window.innerHeight;
    if (this.id || this.isWithoutId) {
      this.loadData();
    }
  }
}
